import type { TradingSignal } from '../types'
import { formatPnlPct } from './pnl'

// Signal P&L from the position's side, in one place. A SHORT profits when the
// price falls, so its P&L is the price change with the sign flipped: a short
// whose coin has dropped 2% reads +2% (green), never -2%.

export type Direction = 'long' | 'short'

/** P&L % of a position opened at `entry`, marked at `price`; null if either is unusable. */
export const signedPnlPct = (direction: Direction, entry: number, price: number | null | undefined): number | null => {
  if (!(entry > 0) || price == null || !Number.isFinite(price)) return null
  const change = ((price - entry) / entry) * 100
  return direction === 'short' ? -change : change
}

/** A signal's P&L % at `price` (the live price, a level, or its exit). */
export const signalPnlPct = (signal: Pick<TradingSignal, 'signal_type' | 'entry_price'>, price: number | null | undefined) =>
  signedPnlPct(signal.signal_type, signal.entry_price, price)

/** "+2.00%" / "-1.35%" / "—", from the position's side. */
export const formatSignalPnl = (signal: Pick<TradingSignal, 'signal_type' | 'entry_price'>, price: number | null | undefined) => {
  const pnl = signalPnlPct(signal, price)
  return pnl == null ? '—' : formatPnlPct(pnl)
}

/** Reward per unit of risk: distance to target over distance to stop. */
export const rewardRisk = (signal: Pick<TradingSignal, 'entry_price' | 'stop_loss' | 'take_profit'>): number | null => {
  const risk = Math.abs(signal.entry_price - signal.stop_loss)
  const reward = Math.abs(signal.take_profit - signal.entry_price)
  return risk > 0 ? reward / risk : null
}

export type ExitReason = 'target' | 'stop' | 'expired' | 'cancelled' | 'closed'

export const EXIT_REASON_LABEL: Record<ExitReason, string> = {
  target: 'Target hit',
  stop: 'Stop hit',
  expired: 'Expired',
  cancelled: 'Cancelled',
  closed: 'Closed',
}

/**
 * Why a finished signal finished. The API's exit_reason is authoritative
 * (target, stop, or expired -- an expiry now closes at the market price and
 * counts). Rows closed before that field existed fall back to the exit price
 * against the levels. CANCELLED rows are the ones never scored: expiries from
 * before 2026-09-19, which were cancelled with no result, and duplicates
 * cleaned up by hand.
 */
export const exitReason = (
  signal: Pick<TradingSignal, 'status' | 'signal_type' | 'exit_price' | 'stop_loss' | 'take_profit' | 'exit_reason'>
): ExitReason | null => {
  if (signal.status === 'cancelled') return 'cancelled'
  if (signal.status !== 'closed') return null
  if (signal.exit_reason === 'target' || signal.exit_reason === 'stop' || signal.exit_reason === 'expired') {
    return signal.exit_reason
  }
  const exit = signal.exit_price
  if (exit == null) return 'closed'
  if (signal.signal_type === 'long') {
    if (exit >= signal.take_profit) return 'target'
    if (exit <= signal.stop_loss) return 'stop'
  } else {
    if (exit <= signal.take_profit) return 'target'
    if (exit >= signal.stop_loss) return 'stop'
  }
  return 'closed'
}

export interface PricePathPositions {
  stop: number
  entry: number
  target: number
  current: number | null
}

/**
 * Where each level sits on the price-path bar, as 0–100%. The axis runs from
 * the lowest level minus 10% of the range to the highest plus 10%, so no
 * marker sits on an edge. Prices increase left to right, which puts a SHORT's
 * target left of its entry and its stop to the right.
 */
export const pricePathPositions = (
  signal: Pick<TradingSignal, 'entry_price' | 'stop_loss' | 'take_profit'>,
  current: number | null | undefined
): PricePathPositions => {
  const levels = [signal.stop_loss, signal.entry_price, signal.take_profit]
  const live = current != null && Number.isFinite(current) ? current : null
  if (live != null) levels.push(live)
  const min = Math.min(...levels)
  const max = Math.max(...levels)
  const range = max - min || Math.abs(signal.entry_price) * 0.01 || 1
  const lo = min - range * 0.1
  const hi = max + range * 0.1
  const at = (v: number) => ((v - lo) / (hi - lo)) * 100
  return {
    stop: at(signal.stop_loss),
    entry: at(signal.entry_price),
    target: at(signal.take_profit),
    current: live == null ? null : at(live),
  }
}

/**
 * How a close under the level rule was filled, for its receipt; null for
 * earlier closes (shown exactly as recorded) and expiries.
 */
export const fillMethodLabel = (
  signal: Pick<TradingSignal, 'exit_fill_method' | 'exit_reason'>
): string | null => {
  switch (signal.exit_fill_method) {
    case 'level':
      if (signal.exit_reason === 'target') return 'Filled at target'
      if (signal.exit_reason === 'stop') return 'Filled at stop'
      return null
    case 'gap_open':
      return 'Gapped: filled at open'
    case 'tick_fallback':
      return 'At the 5-minute price check (1-minute prices unavailable)'
    default:
      return null
  }
}
